import { useEffect, useState, useCallback, useRef } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import {
  endContentBreakGlass,
  extendContentBreakGlass,
  getBreakGlassStatus,
  type BreakGlassReason,
} from '../../../services/ownerConsoleService'
import { navigateOwner, parseOwnerRoute } from '../ownerRouter'

export interface ActiveBreakGlassSession {
  grantId: string
  revealToken: string
  subject: string
  reason: BreakGlassReason
  linkedId: string
  expiresAt: number
}

interface OwnerBreakGlassBannerProps {
  activeSession: ActiveBreakGlassSession | null
  onSessionEnded: () => void
  onSessionUpdated: (session: ActiveBreakGlassSession) => void
  queryBg?: string
  queryFor?: string
}

export const OwnerBreakGlassBanner = ({
  activeSession,
  onSessionEnded,
  onSessionUpdated,
  queryBg,
  queryFor,
}: OwnerBreakGlassBannerProps) => {
  const { t } = useNls()
  const [remainingSec, setRemainingSec] = useState<number | null>(null)
  const [prevExpiresAt, setPrevExpiresAt] = useState<number | undefined>(activeSession?.expiresAt)
  if (prevExpiresAt !== activeSession?.expiresAt) {
    setPrevExpiresAt(activeSession?.expiresAt)
    if (!activeSession?.expiresAt) {
      setRemainingSec(null)
    }
  }

  const [extending, setExtending] = useState(false)
  const isEndingRef = useRef(false)

  const subject = activeSession?.subject || queryFor || 'workspace content'

  const clearBannerQuery = useCallback(() => {
    try {
      sessionStorage.removeItem('owner_breakglass_grant_id')
      sessionStorage.removeItem('owner_breakglass_token')
    } catch {
      // ignore
    }
    const current = parseOwnerRoute(
      window.location.pathname,
      window.location.search,
    )
    if (current.query?.bg || current.query?.for) {
      const nextQuery = { ...current.query }
      delete nextQuery.bg
      delete nextQuery.for
      navigateOwner({ ...current, query: nextQuery })
    }
  }, [])

  // Re-attest server side on refresh if query param has ?bg=1
  useEffect(() => {
    if (queryBg === '1' && !activeSession) {
      let storedGrantId = ''
      let storedToken = ''
      try {
        storedGrantId = sessionStorage.getItem('owner_breakglass_grant_id') || ''
        storedToken = sessionStorage.getItem('owner_breakglass_token') || ''
      } catch {
        // storage disabled or error
      }

      if (storedGrantId) {
        void getBreakGlassStatus({
          grantId: storedGrantId,
          revealToken: storedToken,
          subject: queryFor,
        }).then((res) => {
          if (res.active && res.grantId && res.expiresAt) {
            onSessionUpdated({
              grantId: res.grantId,
              revealToken: storedToken,
              subject: res.subject || queryFor || '',
              reason: res.reason || 'SUPPORT_TICKET',
              linkedId: res.linkedId || '',
              expiresAt: res.expiresAt,
            })
          } else {
            // Expired or invalid on server - clear query
            clearBannerQuery()
            onSessionEnded()
          }
        }).catch(() => {
          clearBannerQuery()
          onSessionEnded()
        })
      } else {
        clearBannerQuery()
        onSessionEnded()
      }
    }
  }, [queryBg, queryFor, activeSession, onSessionEnded, onSessionUpdated, clearBannerQuery])

  const handleEndNow = useCallback(async () => {
    if (isEndingRef.current) return
    isEndingRef.current = true
    if (activeSession?.grantId) {
      try {
        await endContentBreakGlass({ grantId: activeSession.grantId })
      } catch (err) {
        console.warn('[OwnerBreakGlassBanner] Failed to end break glass:', err)
      }
    }
    clearBannerQuery()
    onSessionEnded()
    isEndingRef.current = false
  }, [activeSession, clearBannerQuery, onSessionEnded])

  const handleExtend = useCallback(async () => {
    if (!activeSession?.grantId || extending) return
    setExtending(true)
    try {
      const res = await extendContentBreakGlass({
        grantId: activeSession.grantId,
        extraDurationSec: 300,
      })
      if (res.ok && res.expiresAt) {
        const updated: ActiveBreakGlassSession = {
          ...activeSession,
          revealToken: res.revealToken || activeSession.revealToken,
          expiresAt: res.expiresAt,
        }
        try {
          sessionStorage.setItem('owner_breakglass_token', updated.revealToken)
        } catch {
          // ignore
        }
        onSessionUpdated(updated)
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : ''
      alert(message || 'Failed to extend break glass. If >15 minutes total, submit a new reason.')
    } finally {
      setExtending(false)
    }
  }, [activeSession, extending, onSessionUpdated])

  // Countdown clock
  useEffect(() => {
    if (!activeSession?.expiresAt) {
      return
    }

    const tick = () => {
      const sec = Math.max(0, Math.floor((activeSession.expiresAt - Date.now()) / 1000))
      setRemainingSec(sec)
      if (sec <= 0) {
        void handleEndNow()
      }
    }

    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [activeSession?.expiresAt, handleEndNow])

  if (!activeSession && queryBg !== '1') {
    return null
  }

  const mm = remainingSec !== null ? Math.floor(remainingSec / 60) : 0
  const ss = remainingSec !== null ? String(remainingSec % 60).padStart(2, '0') : '00'
  const timeFormatted = `${mm}:${ss}`

  return (
    <aside className="ownerBreakGlassBanner" role="status" aria-live="polite">
      <div className="ownerBreakGlassBanner__left">
        <span>
          {t('NLSOwnerConsoleBreakGlassActive', {
            subject,
            time: timeFormatted,
          })}
        </span>
      </div>
      <div className="ownerBreakGlassBanner__actions">
        <button
          type="button"
          className="ownerBreakGlassBanner__btn ownerBreakGlassBanner__btn--end"
          onClick={() => void handleEndNow()}
        >
          {t('NLSOwnerConsoleBreakGlassEndNow')}
        </button>
        <button
          type="button"
          className="ownerBreakGlassBanner__btn ownerBreakGlassBanner__btn--extend"
          onClick={() => void handleExtend()}
          disabled={extending}
        >
          {extending ? t('NLSOwnerConsoleBreakGlassExtending') : t('NLSOwnerConsoleBreakGlassExtend')}
        </button>
      </div>
    </aside>
  )
}
